'use client';

import { c as _c } from "react/compiler-runtime";
import './child-bio-image.scss';
import { useTranslation } from 'react-i18next';
import { HtmlBlock } from '@sola/ark-core/atoms/html/html.tsx';
import { ChildImageVariantEnum, getChildImageUrl } from '@sola/ark-core/utils/child-image.ts';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import type { ChildAvailabilityItem, ChildDetails } from '@sola/schema/dotcom/children/child.ts';
import { Image } from '../../atoms/image/image.tsx';
import { useSacImageAltText } from '../../utils/alt-text.ts';
import placeholder from './placeholder.png';
type ChildBioImageProps = {
  availability: ChildAvailabilityItem;
  details: ChildDetails;
  aspectRatio?: string;
  aspectRatioSm?: string;
  aspectRatioMd?: string;
  aspectRatioLg?: string;
  childImageVariant?: ChildImageVariantEnum;
  caption?: string;
  centerCaption?: boolean;
  width?: number;
  fullWidth?: boolean;
};
export const ChildBioImage = t0 => {
  const $ = _c(45);
  const {
    details,
    availability,
    aspectRatio: t1,
    aspectRatioSm,
    aspectRatioMd,
    aspectRatioLg,
    childImageVariant: t2,
    caption,
    centerCaption: t3,
    width: t4,
    fullWidth: t5
  } = t0;
  const aspectRatio = t1 === undefined ? "3-4" : t1;
  const childImageVariant = t2 === undefined ? ChildImageVariantEnum.Full : t2;
  const centerCaption = t3 === undefined ? false : t3;
  const width = t4 === undefined ? 500 : t4;
  const fullWidth = t5 === undefined ? false : t5;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = ["bio", "children", "geo"];
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  const {
    t
  } = useTranslation(t6);
  const {
    imageUrl,
    name,
    hangul
  } = details;
  const {
    gender,
    isSpecialNeeds,
    isOrphan,
    isInHivAffectedArea,
    minDaysWaiting,
    icp: t7
  } = availability;
  const {
    country
  } = t7;
  let t8;
  if ($[1] !== childImageVariant || $[2] !== imageUrl || $[3] !== width) {
    t8 = getChildImageUrl(imageUrl, childImageVariant, width);
    $[1] = childImageVariant;
    $[2] = imageUrl;
    $[3] = width;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  const childImage = t8;
  const titleAndAlt = useSacImageAltText(gender, country, hangul, minDaysWaiting, name.preferred, isOrphan, isSpecialNeeds, isInHivAffectedArea);
  let t9;
  if ($[5] !== hangul.preferred || $[6] !== minDaysWaiting || $[7] !== name.preferred || $[8] !== t) {
    t9 = t("bio:waitingCaption_short", {
      childName: name.preferred,
      koreanName: hangul.preferred,
      days: minDaysWaiting
    });
    $[5] = hangul.preferred;
    $[6] = minDaysWaiting;
    $[7] = name.preferred;
    $[8] = t;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const captionShort = t9;
  let t10;
  if ($[10] !== hangul.preferred || $[11] !== minDaysWaiting || $[12] !== name.preferred || $[13] !== t) {
    t10 = t("bio:waitingCaption", {
      childName: name.preferred,
      koreanName: hangul.preferred,
      days: minDaysWaiting
    });
    $[10] = hangul.preferred;
    $[11] = minDaysWaiting;
    $[12] = name.preferred;
    $[13] = t;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const captionFull = t10;
  const captionText = caption ?? (!fullWidth ? captionFull : captionShort);
  let t11;
  if ($[15] !== fullWidth) {
    t11 = cn("ChildBioImage xl:pr-comfy-xl", {
      "ChildBioImage--fullWidth": fullWidth
    });
    $[15] = fullWidth;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = childImage || placeholder;
  let t13;
  if ($[17] !== t12 || $[18] !== titleAndAlt) {
    t13 = {
      url: t12,
      alt: titleAndAlt
    };
    $[17] = t12;
    $[18] = titleAndAlt;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== aspectRatio || $[21] !== aspectRatioLg || $[22] !== aspectRatioMd || $[23] !== aspectRatioSm || $[24] !== childImage || $[25] !== t13) {
    t14 = <Image key={childImage} image={t13} lazyLoad="eager" aspectRatio={aspectRatio} aspectRatioSm={aspectRatioSm} aspectRatioMd={aspectRatioMd} aspectRatioLg={aspectRatioLg} className="ChildBioImage__Figure__img" />;
    $[20] = aspectRatio;
    $[21] = aspectRatioLg;
    $[22] = aspectRatioMd;
    $[23] = aspectRatioSm;
    $[24] = childImage;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== centerCaption || $[28] !== fullWidth) {
    t15 = cn("ChildBioImage__Figure__figcaption label label-xl", {
      "text-center": centerCaption,
      "text-center sm:text-left": fullWidth
    });
    $[27] = centerCaption;
    $[28] = fullWidth;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== caption || $[31] !== captionFull || $[32] !== captionShort || $[33] !== captionText || $[34] !== fullWidth) {
    t16 = fullWidth && !caption ? <><div className="sm:hidden"><HtmlBlock htmlString={captionShort} /></div><div className="hidden sm:block"><HtmlBlock htmlString={captionFull} /></div></> : <HtmlBlock htmlString={captionText} />;
    $[30] = caption;
    $[31] = captionFull;
    $[32] = captionShort;
    $[33] = captionText;
    $[34] = fullWidth;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t15 || $[37] !== t16) {
    t17 = <figcaption className={t15}>{t16}</figcaption>;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t14 || $[40] !== t17) {
    t18 = <figure className="ChildBioImage__Figure">{t14}{t17}</figure>;
    $[39] = t14;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t11 || $[43] !== t18) {
    t19 = <div className={t11}>{t18}</div>;
    $[42] = t11;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  return t19;
};